import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';

// 模块化子路由
import { approvalRoutes } from './modules/approval';
import { baseDataRoutes } from './modules/base';
import { payrollRoutes } from './modules/payroll';
import { reportRoutes } from './modules/report';
import { systemRoutes } from './modules/system';
import { taskRoutes } from './modules/task';

const Login = () => import('@/components/Login.vue');

// 常量路由（无需权限即可访问）
export const constantRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: '登录', hidden: true },
  },
  {
    path: '/redirect-home',
    name: 'RedirectHome',
    component: DefaultLayout,
    meta: { title: '首页跳转中', hidden: true },
  },
  {
    path: '/',
    component: DefaultLayout,
    children: [
      {
        path: 'unauthorized',
        name: 'Unauthorized',
        component: () => import('@/components/Unauthorized.vue'),
        meta: { title: '403', titleKey: 'route.unauthorized', hidden: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/components/NotFound.vue'),
    meta: { title: '404', hidden: true },
  },
];

// 需要根据权限动态挂载的异步业务路由
export const asyncRoutes: RouteRecordRaw[] = [
  reportRoutes,
  baseDataRoutes,
  taskRoutes,
  payrollRoutes,
  systemRoutes,
  approvalRoutes,
];

import { authService } from '@/services/authService';
import { timeService } from '@/services/timeService';
import { useAppStore } from '@/stores/app';
import { useApprovalStore } from '@/stores/approval';
import { buildMenus, resolveHomePath } from '@/utils/menu';
import { permissionGuard } from '@/utils/permission';

// 初始直接挂载常量路由 + 一个用于首页重定向的占位路由 + 异步权限路由。
// 不做动态添加，改为登录后根据权限计算允许的路由并构建菜单
const router = createRouter({
  history: createWebHashHistory(),
  routes: [...constantRoutes, ...asyncRoutes],
});

let asyncAdded = false;

// 过滤函数：判断当前用户是否能访问该路由
interface AccessMeta {
  // roles 已不再参与过滤，仅保留兼容（不使用）
  roles?: string[];
  permissions?: string[];
  requiresApprover?: boolean;
  requiresAllConditions?: boolean; // true 时 permissions 和 requiresApprover 必须同时满足
}

function isNonEmptyArray<T>(val: unknown): val is T[] {
  return Array.isArray(val) && val.length > 0;
}

function canAccess(route: RouteRecordRaw, _userRoles: string[], userPerms: string[]): boolean {
  // 基于 permissions 与 requiresApprover 的逻辑；roles 已弃用
  const meta = (route.meta ?? {}) as AccessMeta;
  const conditions: boolean[] = [];

  if (isNonEmptyArray<string>(meta.permissions)) {
    const permOk = meta.permissions.some((p) => userPerms.includes(p));
    conditions.push(permOk);
  }
  if (meta.requiresApprover) {
    const s = useApprovalStore();
    conditions.push(s.isCurrentUserApprover());
  }

  if (conditions.length === 0) return true; // 若未设置上述条件，则默认放行

  // requiresAllConditions: true 时需要所有条件都满足（AND），否则任一满足即可（OR）
  return meta.requiresAllConditions ? conditions.every(Boolean) : conditions.some(Boolean);
}

// 递归过滤异步路由
function filterAsyncRoutes(
  routes: RouteRecordRaw[],
  _userRoles: string[], // roles 已弃用
  userPerms: string[],
): RouteRecordRaw[] {
  const res: RouteRecordRaw[] = [];
  routes.forEach((r) => {
    const clone: RouteRecordRaw = { ...r };
    if (canAccess(clone, [], userPerms)) {
      if (clone.children) {
        clone.children = filterAsyncRoutes(clone.children, [], userPerms);
        if (clone.children.length === 0) {
          return;
        }
      }
      res.push(clone);
    }
  });
  return res;
}

// 对外暴露：在用户登录并拿到权限后调用
export function computeAllowedRoutes(userPerms: string[]) {
  // 仅根据权限过滤菜单
  const routes = filterAsyncRoutes(asyncRoutes, [], userPerms);
  asyncAdded = true;
  return routes;
}

// 重置路由 - 用户退出登录时调用
export function resetRouter(): void {
  // 退出登录时重置 asyncAdded 标记，才能在下一次登录时重新加载路由权限
  asyncAdded = false;
}

let initialized = false;

router.beforeEach(async (to, from, next) => {
  console.log('Route guard:', to);
  if (to.name === 'Login') return next();
  const { useUserStore } = await import('@/stores/user');
  const userStore = useUserStore();
  const appStore = useAppStore();
  // 每次刷新或首次跳转时从本地初始化
  if (!initialized) {
    userStore.initFromStorage();
    initialized = true;
  }
  // 未登录（无 token 或无 roleId）直接去登录
  if (!userStore.token || !userStore.roleId) {
    return next({ path: '/login' });
  }

  console.log('beforeEach asyncAdded:', asyncAdded);
  // 登录后首次加载或刷新后，统一从角色权限接口获取权限
  if (!asyncAdded) {
    try {
      // 初始化系统时间偏移
      await timeService.init();
      const perms = await authService.getRolePermissions(userStore.roleId);
      // 将后端权限名映射到前端 PERMS 常量值或直接作为字符串存储
      userStore.permissions = perms;
      // 预加载审批人集合（用于 requiresApprover 过滤）
      try {
        const { useApprovalStore } = await import('@/stores/approval');
        const approvalStore = useApprovalStore();
        await approvalStore.loadAllApprovers();
      } catch {
        /* ignore */
      }
      const allowed = computeAllowedRoutes(perms);
      // 构建菜单
      const menus = buildMenus(allowed);
      appStore.setMenus(menus);
    } catch (e) {
      console.error('加载角色权限失败', e);
      // 回退到本地已有权限
      try {
        const { useApprovalStore } = await import('@/stores/approval');
        const approvalStore = useApprovalStore();
        await approvalStore.loadAllApprovers();
      } catch {
        /* ignore */
      }
      const allowed = computeAllowedRoutes(userStore.permissions);
      const menus = buildMenus(allowed);
      appStore.setMenus(menus);
    }
  }

  // 如果进入 /redirect-home 计算首页并跳转
  if (to.path === '/redirect-home' || to.path === '/') {
    // 如果还没加载菜单（例如刷新后第一次）从当前路由集合构建
    if (appStore.menus.length === 0) {
      const currentAllowed = computeAllowedRoutes(userStore.permissions);
      appStore.setMenus(buildMenus(currentAllowed));
    }
    const menus = appStore.menus;
    if (!menus.length) {
      return next({ name: 'Unauthorized' });
    }
    const home = resolveHomePath(menus);
    if (!home) return next({ name: 'Unauthorized' });
    return next(home);
  }

  permissionGuard(to, from, next);
});

export default router;
